import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, EventEmitter, HostListener, Inject, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, SortDirection } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { IManageAudienceUserActionPayload } from 'app/library/audience-users/interfaces/audience-user-action.action-payload.interface';
import { IManageAudienceUsersActionPayload } from 'app/library/audience-users/interfaces/audience-users-action.action-payload.interface';
import { AudienceUsersService } from 'app/library/audience-users/services/audience-users.service';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { IManageExperienceUsersActionPayload } from 'app/library/experience-user/interfaces/experience-users-action.action-payload.interface';
import { ExperienceUsersService } from 'app/library/experience-users/services/experience-users.service';
import { ExperienceService } from 'app/library/experience/services/experience.service';
import { ExtendedAttributeCategoryEnum } from 'app/library/extended-attributes/enums/extended-attribute-category.enum';
import { ExtendedAttributeType } from 'app/library/extended-attributes/enums/extended-attribute-type.enum';
import { ExtendedAttribute } from 'app/library/extended-attributes/models/extended-attribute';
import { ExtendedAttributeService } from 'app/library/extended-attributes/services/extended-attribute.service';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { EngageMoveExperienceMembersDialogComponent } from 'app/library/grow/dialogs/engage-move-experience-members-dialog/engage-move-experience-members-dialog.component';
import { GrowMoveAudienceMembersDialogComponent } from 'app/library/grow/dialogs/grow-move-audience-members-dialog/grow-move-audience-members-dialog.component';
import { RefetchUserDirectoryViewsAfterBackgroundJob } from 'app/library/all-users/actions';
import { PasswordlessAccessDialogComponent } from 'app/library/passwordless-access/components/dialogs/passwordless-access-dialog/passwordless-access-dialog.component';
import { IAddTeamLeadersActionPayload } from 'app/library/team/models/add-team-leaders-action.action.payload';
import { IAddTeamMembersActionPayload } from 'app/library/team/models/add-team-members-action.action.payload';
import { IAddTeamWatchersActionPayload } from 'app/library/team/models/add-team-watchers-action.action.payload';
import { Team } from 'app/library/team/models/team.model';
import { UserTeamRoleEnum } from 'app/library/team/models/user-team-role.enum';
import { TeamService } from 'app/library/team/services/team.service';
import { SelectTeamsDialogComponent } from 'app/main/pages/teams/dialogs/select-teams-dialog/select-teams-dialog.component';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { FuseConfigService } from 'app/projects/fuse/src/lib/services/config.service';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { CopyToClipboardDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/copy-to-clipboard/copy-to-clipboard.component';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { ILanguage, ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import { PageSizePreferenceService } from 'app/projects/shared/src/lib/services/page-size-preference.service';
import { DeleteUserFailure } from 'app/projects/user/src/lib/actions/delete-entity-failure.action';
import { DeleteUser } from 'app/projects/user/src/lib/actions/delete-entity.action';
import { ResetPasswordUrl } from 'app/projects/user/src/lib/actions/reset-password.action';
import { ResetPasswordsFailure } from 'app/projects/user/src/lib/actions/reset-passwords-failure.action';
import { ResetPasswordUrlSuccess, ResetPasswordsSuccess } from 'app/projects/user/src/lib/actions/reset-passwords-success.action';
import { ResetPasswords } from 'app/projects/user/src/lib/actions/reset-passwords.action';
import { UpdateUserStatus } from 'app/projects/user/src/lib/actions/update-user-status.action';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserStatuses } from 'app/projects/user/src/lib/models/user-statuses';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { TalkService } from 'app/services/talk.service';
import { Observable, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { FetchAllUsersFailure, FetchAllUsersSuccess, FetchCommunityUsersFailure, FetchCommunityUsersSuccess, SortAllUsers } from '../../actions';
import { IChoosenFilterEntity } from '../../interfaces/choosen-filter.interface';
import { ExtendedAttributeFilterInterface } from '../../interfaces/extended-attribute-filer.interface';
import { ChangeUserEmailAddressDialogComponent } from '../dialog/change-user-email-address/change-user-email-address-dialog.component';
import { AllUserDataSource } from './datasource';

@Component({
    selector: 'app-active-user-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class ActiveUserListComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit {
    @Input()
    possibleTableColumns: {
        id: string;
        translation: string;
    }[] = [];

    @Input()
    isCommunityPage: boolean = false;

    get pageSizePreferenceKey(): string {
        return this.isCommunityPage ? 'all-users.community-active-user-list' : 'all-users.active-user-list';
    }

    @Input()
    entityId: string;

    @Input()
    sortBy: string;

    @Input()
    sortOrder: SortDirection;

    @Input()
    user$: Observable<User[]>;

    @Input()
    loadUser$: Observable<boolean>;

    @Input()
    filteredUser$: Observable<User[]>;

    @Input()
    selectedUserId$: Observable<string[]>;

    @Input()
    searchTerm$: Observable<string>;
    searchTerm: string;

    @Input()
    totalCount$: Observable<number>;

    @Input()
    module$: Observable<IChoosenFilterEntity[]>;
    moduleIds: string[] = [];

    @Input()
    audiences$: Observable<IChoosenFilterEntity[]>;
    audienceIds: string[] = [];

    @Input()
    experiences$: Observable<IChoosenFilterEntity[]>;
    experienceIds: string[] = [];

    @Input()
    teams$: Observable<IChoosenFilterEntity[]>;
    teamIds: string[] = [];
    excludedTeamIds: string[] = [];
    teamRoleIds: string[] = [];

    @Input()
    teamRoleIds$: Observable<string[]>;

    @Input()
    extendedAttributes$: Observable<ExtendedAttributeFilterInterface[]>;
    extendedAttributesFilters: ExtendedAttributeFilterInterface[] = [];

    @Input()
    userRoles$: Observable<IChoosenFilterEntity[]>;
    userRoleIds: string[] = [];

    @Input()
    statuses$: Observable<IChoosenFilterEntity[]>;
    statusIds: string[] = [];

    @Input()
    isUnassignedSelectedModules$: Observable<boolean>;
    isUnassignedSelectedModules: boolean;

    @Input()
    isUnassignedSelectedAudiences$: Observable<boolean>;
    isUnassignedSelectedAudiences: boolean;

    @Input()
    isUnassignedSelectedTeams$: Observable<boolean>;
    isUnassignedSelectedTeams: boolean;

    @Input()
    isUnassignedSelectedExperiences$: Observable<boolean>;
    isUnassignedSelectedExperiences: boolean;

    @Output()
    edit = new EventEmitter<User>();

    @Output()
    select = new EventEmitter<User>();

    @Output()
    toggle = new EventEmitter();

    @Output()
    emitColumns = new EventEmitter<string[]>();

    @Output()
    clearFiltersRequest = new EventEmitter<void>();

    @HostListener('mouseenter', ['$event'])
    onMouseEnter(e: Event) {
        if (e) {
            this.isActionBoxHovered = true;
        }
    }

    @HostListener('mouseleave', ['$event'])
    onMouseLeave(e: Event) {
        if (e) {
            this.isActionBoxHovered = false;
        }
    }

    get hasTenantAdminOrUsersWritePermission(): boolean {
        return this.profile.permissions.includes(AdminPermissions.USERS_WRITE) || this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
    }

    profile = this._store.selectSnapshot(UserState.getMyInfo);

    fuseConfig: any;
    isLoading: boolean = false;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;
    @ViewChild('columnsSelect')
    columnsSelect: MatSelect;

    dataSource: AllUserDataSource;
    displayedColumns: string[] = [];
    selectedUserIds: string[] = [];
    checkboxes: {};
    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    growMoveAudienceUsersDialogRef: MatDialogRef<GrowMoveAudienceMembersDialogComponent>;
    engageMoveExperienceUsersDialogRef: MatDialogRef<EngageMoveExperienceMembersDialogComponent>;
    changeUserEmailAddressDialogRef: MatDialogRef<ChangeUserEmailAddressDialogComponent>;
    assignUserToTeamDialogRef: MatDialogRef<SelectTeamsDialogComponent>;
    passwordlessAccessDialogRef: MatDialogRef<PasswordlessAccessDialogComponent>;
    selectedLanguage: ILanguage = null;

    userStatuses = UserStatuses;
    ExtendedAttributeType = ExtendedAttributeType;
    extendedAttributes: ExtendedAttribute[] = [];
    tableColumnsDropdown = new FormControl(null);
    private _columnsSnapshot: string[] | null = null;
    private _applyClicked = false;
    private _defaultHiddenColumnIds = ['createdAt', 'updatedAt'];

    isEngageFeatureEnabled: boolean;
    isTeamsFeatureEnabled: boolean;
    isSSOFeatureEnabled: boolean;
    isExtendedAttributesFeatureEnabled: boolean;
    isPasswordlessLoginFeatureEnabled: boolean;
    isUserDetailsCardHidden = false;
    isActionBoxHovered: boolean = false;

    private _saveColumnsToSessionStorage(): void {
        sessionStorage.setItem(this.isCommunityPage ? 'communityUsersDisplayedColumns' : 'allUsersDisplayedColumns', JSON.stringify(this.displayedColumns));
    }

    constructor(
        @Inject(MAXBRAIN_LANGUAGE_SERVICE) public languageService: ILanguageService,
        private _store: Store,
        private _action$: Actions,
        public matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _translateService: TranslateService,
        private _talkService: TalkService,
        private _fuseConfigService: FuseConfigService,
        private _experienceService: ExperienceService,
        private _experienceUsersService: ExperienceUsersService,
        private _audienceUsersService: AudienceUsersService,
        private _audienceService: AudienceService,
        private _featureSwitchPipe: FeatureSwitchPipe,
        private _extendedAttributeService: ExtendedAttributeService,
        private _teamService: TeamService,
        private _pageSizePreferenceService: PageSizePreferenceService
    ) {
        super();

        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
            this.fuseConfig = config;
        });

        this.isTeamsFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Teams);
        this.isEngageFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Engage);
        this.isSSOFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Sso);
        this.isExtendedAttributesFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.ExtendedAttributes);
        this.isPasswordlessLoginFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.PasswordlessLogin);
    }

    async ngOnInit(): Promise<void> {
        this.selectedLanguage = this._translateService.currentLang
            ? this.languageService.getLanguageById(this._translateService.currentLang)
            : this.languageService.getDefaultLanguage();

        this.loadUser$
            .pipe(
                filter((resetPageIndex) => resetPageIndex !== null),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((resetPageIndex) => {
                this.loadUsersPage(resetPageIndex);
            });

        this.isUnassignedSelectedModules$?.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            this.isUnassignedSelectedModules = value;
        });

        this.isUnassignedSelectedExperiences$?.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            this.isUnassignedSelectedExperiences = value;
        });

        this.isUnassignedSelectedAudiences$?.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            this.isUnassignedSelectedAudiences = value;
        });

        this.isUnassignedSelectedTeams$?.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            this.isUnassignedSelectedTeams = value;
        });

        this.user$
            .pipe(
                filter((users) => !!users),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((users) => {
                this.checkboxes = {};

                users.map((user) => {
                    this.checkboxes[user.id] = this.selectedUserIds.includes(user.id);
                });
            });

        this.selectedUserId$
            ?.pipe(
                filter((selectedUserIds) => !!selectedUserIds),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((selectedUserIds) => {
                for (const id in this.checkboxes) {
                    if (!this.checkboxes.hasOwnProperty(id)) {
                        continue;
                    }

                    this.checkboxes[id] = selectedUserIds.includes(id);
                }

                this.selectedUserIds = selectedUserIds;
            });

        this._translateService.onLangChange.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ lang }) => {
            this.loadUsersPage(false);
        });

        this._action$.pipe(takeUntil(this._unsubscribeAll), ofActionDispatched(RefetchUserDirectoryViewsAfterBackgroundJob)).subscribe(() => {
            this.loadUsersPage(true);
        });

        // preselect table columns before extended attributes are fetched
        this.setTableColumns();

        if (this.isExtendedAttributesFeatureEnabled) {
            if (this.isCommunityPage) {
                await this._extendedAttributeService.fetchExtendedAttributes([ExtendedAttributeCategoryEnum.USER]);
            } else {
                await this._extendedAttributeService.fetchAdminExtendedAttributes([ExtendedAttributeCategoryEnum.USER]);
            }
            this.extendedAttributes = this._extendedAttributeService.getExtendedAttributes();

            this.extendedAttributes
                .filter((attr) => this.isCommunityPage ? attr.visible : true)
                .forEach((attribute) => {
                    const tempAttribute = {
                        id: attribute.name,
                        translation: attribute.translationKey,
                    };

                    this.possibleTableColumns.splice(this.possibleTableColumns.length - 2, 0, tempAttribute);
                });
        }

        if (!this.isCommunityPage) {
            this.possibleTableColumns.splice(this.possibleTableColumns.length - 2, 0, {
                id: 'userRole',
                translation: 'GENERAL.LABEL.USER_ROLE',
            });
        }

        // Update the user data stream to display relevant information using the selected language
        this.filteredUser$ = this.filteredUser$.pipe(
            map((filteredUsers) =>
                filteredUsers.map((filteredUser) => ({
                    ...filteredUser,
                    analytics: {
                        ...filteredUser.analytics,
                        lastLogin: filteredUser.analytics.lastLogin ? filteredUser.analytics.lastLogin.locale(this.selectedLanguage.id) : null,
                    },
                    resetPasswordLastSent: filteredUser.resetPasswordLastSent ? filteredUser.resetPasswordLastSent.locale(this.selectedLanguage.id) : null,
                    initials: filteredUser.initials,
                    fullName: filteredUser.fullName,
                }))
            )
        );

        this.dataSource = new AllUserDataSource(this.filteredUser$, this._store, this._action$);

        setTimeout(() => {
            this.paginator?.page.subscribe(({ length, pageIndex, pageSize }: PageEvent) => {
                const itemsleft = length - pageIndex * pageSize;
                const itemsInThisPage = itemsleft < pageSize ? itemsleft : pageSize;
                // FIXME: these hardcoded numbers represent the height of the rest of the elements surrounding the table within the layout content
                const tableHeight = 80 + 24 + 64 + 56 + itemsInThisPage * 65 + 72 + 56;
                const contentElementHeight = document.getElementById('container-2').offsetHeight;
                const scrollTop = tableHeight > contentElementHeight ? tableHeight - contentElementHeight : 0;

                setTimeout(() => {
                    document.getElementById('container-3').scrollTop = scrollTop;
                });
            });

            this.sort?.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortAllUsers(sort));

                this.loadUsersPage(true);
            });

            this.paginator?.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadUsersPage(false);
            });

            const preferredPageSize = this._pageSizePreferenceService.getPageSize(this.pageSizePreferenceKey, [10, 25, 50], 50);
            if (this.paginator && this.paginator.pageSize !== preferredPageSize) {
                // _changePageSize emits a page event which triggers loadUsersPage via the subscription above
                this.paginator._changePageSize(preferredPageSize);
            } else {
                // When preferred size already matches, page event is not emitted — load explicitly
                this.loadUsersPage(true);
            }
        });

        // TODO: Fix _loadingSubject in datasource and remove this subscription
        this._action$
            .pipe(ofActionDispatched(FetchAllUsersSuccess, FetchAllUsersFailure, FetchCommunityUsersSuccess, FetchCommunityUsersFailure), takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.isLoading = false;
            });

        this.emitColumns.emit(this.displayedColumns);
    }

    ngAfterViewInit(): void {
        combineLatest([this.module$, this.audiences$, this.experiences$, this.teams$, this.extendedAttributes$, this.userRoles$, this.statuses$, this.teamRoleIds$])
            .pipe(
                filter(([selectedModules, selectedAudiences, selectedExperiences, selectedTeams, extendedAttributes, selectedUserRoles, selectedStatuses, selectedTeamRoleIds], index) => {
                    this.moduleIds = [];
                    if (selectedModules?.length) {
                        selectedModules.forEach((selectedModule) => {
                            this.moduleIds.push(selectedModule.id);
                        });
                    }

                    this.audienceIds = [];
                    if (selectedAudiences?.length) {
                        selectedAudiences.forEach((selectAudience) => {
                            this.audienceIds.push(selectAudience.id);
                        });
                    }

                    this.teamIds = [];
                    this.excludedTeamIds = [];
                    if (selectedTeams?.length) {
                        selectedTeams.forEach((selectTeam) => {
                            this.teamIds.push(selectTeam.id);
                            this.excludedTeamIds = [...this.excludedTeamIds, ...(selectTeam.excludedTeamIds || [])];
                        });
                        this.excludedTeamIds = Array.from(new Set(this.excludedTeamIds));
                    }

                    this.experienceIds = [];
                    if (selectedExperiences?.length) {
                        selectedExperiences.forEach((selectExperience) => {
                            this.experienceIds.push(selectExperience.id);
                        });
                    }

                    this.userRoleIds = [];
                    if (selectedUserRoles?.length) {
                        selectedUserRoles.forEach((selectUserRole) => {
                            this.userRoleIds.push(selectUserRole.id);
                        });
                    }

                    this.statusIds = [];
                    if (selectedStatuses?.length) {
                        selectedStatuses.forEach((selectStatus) => {
                            this.statusIds.push(selectStatus.id);
                        });
                    }

                    this.extendedAttributesFilters = [];
                    if (extendedAttributes?.length) {
                        this.extendedAttributesFilters = extendedAttributes;
                    }

                    this.teamRoleIds = selectedTeamRoleIds?.length ? selectedTeamRoleIds : [];

                    return index > 0;
                }),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                setTimeout(() => {
                    this.loadUsersPage(true);
                }, 200);
            });

        this.searchTerm$
            .pipe(
                filter((searchTerm, index) => {
                    this.searchTerm = searchTerm || '';
                    return index > 0;
                }),
                debounceTime(500),
                distinctUntilChanged(),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this.loadUsersPage(true);
            });
    }

    setTableColumns(): void {
        const key = this.isCommunityPage ? 'communityUsersDisplayedColumns' : 'allUsersDisplayedColumns';
        const storedColumns = JSON.parse(sessionStorage.getItem(key));
        const defaultColumns = this.possibleTableColumns.map((column) => column.id).filter((columnId) => !this._defaultHiddenColumnIds.includes(columnId));
        this.displayedColumns = storedColumns || defaultColumns;

        if (this.hasTenantAdminOrUsersWritePermission) {
            // Only add 'checkbox' and 'buttons' columns if permission allows
            if (!this.displayedColumns.includes('checkbox')) {
                this.displayedColumns.unshift('checkbox');
            }
        } else {
            // Remove 'checkbox' and 'buttons' if user lacks permission
            this.displayedColumns = this.displayedColumns.filter((col) => col !== 'checkbox');
        }

        this.tableColumnsDropdown.setValue(this.displayedColumns);
    }

    onColumnsDropdownOpenedChange(opened: boolean): void {
        if (opened) {
            this._columnsSnapshot = [...this.displayedColumns];
        } else {
            if (this._applyClicked) {
                this._applyClicked = false;
            } else if (this._columnsSnapshot) {
                this.tableColumnsDropdown.setValue([...this._columnsSnapshot], { emitEvent: false });
            }
            this._columnsSnapshot = null;
        }
    }

    applyColumns(): void {
        this._applyClicked = true;
        const currentValue = this.tableColumnsDropdown.value;
        if (currentValue) {
            this.displayedColumns = [...currentValue];
            this.emitColumns.emit(this.displayedColumns);
            this.loadUsersPage();
            this._saveColumnsToSessionStorage();
        }
        this.columnsSelect.close();
    }

    drop(event: CdkDragDrop<string[]>) {
        // use 'adjustment' because first one or two columns are not draggable
        const adjustment = this.isCommunityPage ? 1 : 2;
        moveItemInArray(this.displayedColumns, event.previousIndex + adjustment, event.currentIndex + adjustment);
        this._saveColumnsToSessionStorage();
    }

    changeEmailAddress(user: User): void {
        this.changeUserEmailAddressDialogRef = this.matDialog.open(ChangeUserEmailAddressDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        this.changeUserEmailAddressDialogRef.componentInstance.user = user;
    }

    resetPassword(user: User): void {
        this._store.dispatch(new ResetPasswords([user.id]));
        this._action$.pipe(ofActionDispatched(ResetPasswordsSuccess, ResetPasswordsFailure), take(1), takeUntil(this._unsubscribeAll)).subscribe((action) => {
            if (action instanceof ResetPasswordsSuccess) {
                const successMessage = this._translateService.instant('USER.MESSAGE.RESET_PASSWORDS_SUCCESS', { total: 1 });

                this._matSnackBar.open(successMessage, 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.medium,
                });

                this.loadUsersPage(true);
            }
        });
    }

    getResetPasswordUrl(user: User): void {
        this._store.dispatch(new ResetPasswordUrl([user.id]));
        this._action$.pipe(ofActionDispatched(ResetPasswordUrlSuccess), take(1), takeUntil(this._unsubscribeAll)).subscribe((action: ResetPasswordUrlSuccess) => {
            const dialogRef = this.matDialog.open(CopyToClipboardDialogComponent, {
                width: '600px',
            });
            dialogRef.componentInstance.title = 'USER.LABEL.PASSWORD_RESET_LINK';
            dialogRef.componentInstance.placeholder = 'USER.LABEL.PASSWORD_RESET_LINK';
            dialogRef.componentInstance.actionButton = 'GENERAL.BUTTON.COPY';
            dialogRef.componentInstance.textToCopy = action.payload[0].resetUrl;
        });
    }

    /**
     * Deactivate User
     */
    deactivateUser(user: User): void {
        this.confirmDialogRef = this.matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('USER.MESSAGE.DEACTIVATE_CONFIRMATION', user);

        this.confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this._store.dispatch(new UpdateUserStatus(Object.assign({}, user, { status: 'inactive' })));
            }

            this.confirmDialogRef = null;
        });
    }

    /**
     * Activate User
     */
    activateUser(user: User): void {
        this.confirmDialogRef = this.matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('USER.MESSAGE.ACTIVATE_CONFIRMATION', user);

        this.confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this._store.dispatch(new UpdateUserStatus(Object.assign({}, user, { status: 'active' })));
            }

            this.confirmDialogRef = null;
        });
    }

    /**
     * Delete User
     */
    deleteUser(user: User): void {
        this.confirmDialogRef = this.matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('USER.MESSAGE.DELETE_CONFIRMATION', user);
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.DELETE';
        this.confirmDialogRef.componentInstance.typedConfirmationKeyword = this._translateService.instant('USER.DIALOG.DELETE.CONFIRM_KEYWORD');

        this.confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this._store.dispatch(new DeleteUser(user.id));

                this._action$.pipe(ofActionDispatched(DeleteUserFailure), take(1)).subscribe(() => {
                    const failureMessage = this._translateService.instant('ERROR.RESPONSE.only_cockpit_user_can_delete_other_cockpit_user');

                    this._matSnackBar.open(failureMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });
                });
            }

            this.confirmDialogRef = null;
        });
    }

    async assignUserToAudience(user: User): Promise<void> {
        let audiences = this._audienceService.getAudiences();
        if (!audiences) {
            await this._audienceService.fetchAudiences();
            audiences = this._audienceService.getAudiences();
        }

        this.growMoveAudienceUsersDialogRef = this.matDialog.open(GrowMoveAudienceMembersDialogComponent, {
            disableClose: false,
            width: '680px',
            autoFocus: false,
        });
        const audienceListTree = this._audienceService.convertListToTree(audiences);
        this.growMoveAudienceUsersDialogRef.componentInstance.title = 'ENGAGE.AUDIENCE.DIALOGS.MOVE_USER.SINGULAR';
        this.growMoveAudienceUsersDialogRef.componentInstance.label = 'ENGAGE.AUDIENCE.DIALOGS.MOVE_USER.LABEL';
        this.growMoveAudienceUsersDialogRef.componentInstance.audienceListTree = audienceListTree[0];
        this.growMoveAudienceUsersDialogRef.componentInstance.selectedUserId$ = this.selectedUserId$;

        this.growMoveAudienceUsersDialogRef.afterClosed().subscribe(async (result: IManageAudienceUsersActionPayload) => {
            if (result) {
                const payload: IManageAudienceUserActionPayload = {
                    users: [{ id: user.id }],
                    audiences: [{ id: result.target }],
                };

                const responsePayload = await this._audienceUsersService.assignUserToAudience(payload);
                const isUserNewlyAssigned = responsePayload.newlyAssigned.length > 0;
                const isUserAlreadyyAssigned = responsePayload.alreadyAssigned.length > 0;
                if (isUserNewlyAssigned) {
                    const successMessage = this._translateService.instant('ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_AUDIENCE.MESSAGE.ASSIGN_USER_SUCCESS');
                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });
                    this.clearFiltersRequest.emit();
                } else if (isUserAlreadyyAssigned) {
                    const successMessage = this._translateService.instant('ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_AUDIENCE.MESSAGE.ALREADY_ASSIGNED_USER_SUCCESS');
                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });
                }
            }
        });
    }

    async assignUserToExperience(user: User): Promise<void> {
        let experiences = this._experienceService.getExperiences();
        if (!experiences) {
            await this._experienceService.fetchExperiences();
            experiences = this._experienceService.getExperiences();
        }

        this.engageMoveExperienceUsersDialogRef = this.matDialog.open(EngageMoveExperienceMembersDialogComponent, {
            disableClose: false,
            width: '600px',
        });
        this.engageMoveExperienceUsersDialogRef.componentInstance.title = 'ENGAGE.EXPERIENCE.DIALOGS.MOVE_USER.SINGULAR';
        this.engageMoveExperienceUsersDialogRef.componentInstance.label = 'ENGAGE.EXPERIENCE.DIALOGS.MOVE_USER.LABEL';
        this.engageMoveExperienceUsersDialogRef.componentInstance.experiences = experiences;
        this.engageMoveExperienceUsersDialogRef.componentInstance.selectedUserId$ = this.selectedUserId$;

        this.engageMoveExperienceUsersDialogRef.afterClosed().subscribe(async (result: { target: string; list: User[] }) => {
            if (result) {
                const payload: IManageExperienceUsersActionPayload = {
                    users: [{ id: user.id }],
                    experience: { id: result.target },
                };
                const responsePayload = await this._experienceUsersService.addUsersToExperience(payload);
                if (responsePayload) {
                    if (responsePayload.asyncJobId) {
                        this._matSnackBar.open(this._translateService.instant('USER.MESSAGE.BULK_OPERATION_QUEUED'), 'OK', {
                            verticalPosition: 'top',
                            duration: SnackBarTime.medium,
                        });
                    } else {
                        const alreadyAssigned = responsePayload.already_assigned.length;
                        const newlyAssigned = responsePayload.newly_assigned.length;
                        const successMessage = this._translateService.instant('ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_EXPERIENCE.MESSAGE.ASSIGN_SUCCESS', {
                            alreadyAssigned,
                            newlyAssigned,
                        });
                        this._matSnackBar.open(successMessage, 'OK', {
                            verticalPosition: 'top',
                            duration: SnackBarTime.medium,
                        });
                    }
                    this.clearFiltersRequest.emit();
                } else {
                    const errorMessage = this._translateService.instant('ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_EXPERIENCE.MESSAGE.ASSIGN_SINGULAR_FAILURE');
                    this._matSnackBar.open(errorMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });
                }
            }
        });
    }

    async openAssignUserToTeamDialog(user: User): Promise<void> {
        await this._teamService.fetchRootTeams();

        this.assignUserToTeamDialogRef = this.matDialog.open(SelectTeamsDialogComponent, {
            disableClose: false,
            width: '760px',
            autoFocus: false,
        });

        const rootTeams = this._teamService.getRootTeams();
        const listTree = rootTeams.map((team) => ({
            id: team.id,
            name: team.name,
            children: [],
            childrenCount: team.subteamCount || 0,
            parent: team.parent,
            isSelected: false,
            isParentSelected: false,
        }));

        this.assignUserToTeamDialogRef.componentInstance.title = this._translateService.instant('USER.DIALOG.ASSIGN_TO_TEAM.TITLE', {
            userCount: this.selectedUserIds.length,
        });
        this.assignUserToTeamDialogRef.componentInstance.listTree = listTree;
        this.assignUserToTeamDialogRef.componentInstance.showSelectRole = true;
        this.assignUserToTeamDialogRef.componentInstance.initDialog();

        this.assignUserToTeamDialogRef.afterClosed().subscribe(async (data: { selectedTeams: Team[]; selectedRole: number }) => {
            if (data) {
                const payload: IAddTeamLeadersActionPayload | IAddTeamMembersActionPayload | IAddTeamWatchersActionPayload = {
                    userIds: [user.id],
                    teamIds: data.selectedTeams.map((selectedTeam) => selectedTeam.id),
                };

                let result = null;
                switch (data.selectedRole) {
                    case UserTeamRoleEnum.LEAD:
                        result = await this._teamService.addUsersToTeamAsLeaders(payload);
                        break;
                    case UserTeamRoleEnum.MEMBER:
                        result = await this._teamService.addUsersToTeamAsMembers(payload);
                        break;
                    case UserTeamRoleEnum.WATCHER:
                        result = await this._teamService.addUsersToTeamAsWatchers(payload);
                        break;
                    default:
                        break;
                }

                if (
                    result &&
                    (data.selectedRole !== UserTeamRoleEnum.MEMBER ||
                        (result as { asyncJobId?: string; teamMembers?: unknown[] }).asyncJobId ||
                        ((result as { teamMembers?: unknown[] }).teamMembers?.length ?? 0) > 0)
                ) {
                    const successMessage = this._translateService.instant('USER.DIALOG.ASSIGN_TO_TEAM.MESSAGE.SUCCESS');

                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });
                    this.clearFiltersRequest.emit();
                }
            }
            this.assignUserToTeamDialogRef = null;
        });
    }

    openPasswordlessAccessDialog(user: User): void {
        this.passwordlessAccessDialogRef = this.matDialog.open(PasswordlessAccessDialogComponent, {
            disableClose: false,
            width: '770px',
        });

        this.passwordlessAccessDialogRef.componentInstance.selectedUserIds = [user.id];
    }

    /** Whether the number of selected elements matches the total number of rows. */
    get isAllSelected(): boolean {
        if (this.paginator) {
            return this.paginator.pageIndex < this.paginator.getNumberOfPages() - 1
                ? this.selectedUserIds.length === this.paginator.pageSize
                : this.selectedUserIds.length === this.paginator.length - this.paginator.pageIndex * this.paginator.pageSize;
        }
    }

    goToUserProfile(userId: string): void {
        if (!this.isCommunityPage) {
            this.isUserDetailsCardHidden = true;
            this._store.dispatch(new Navigate(['/users/' + userId + '/edit']));
        }
    }

    loadUsersPage(resetPageIndex = false): void {
        this.isLoading = true;

        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        if (this.isCommunityPage) {
            this.dataSource.loadUsers(
                'communityUsers',
                null,
                this.entityId,
                this.searchTerm,
                this.sort?.active,
                this.sort?.direction,
                this.paginator?.pageSize,
                this.paginator?.pageIndex + 1,
                null,
                null,
                true,
                null,
                null,
                null,
                null,
                this.extendedAttributesFilters,
                null,
                null,
                null,
                this.displayedColumns,
                null
            );
        } else {
            this.dataSource.loadUsers(
                'users',
                this.moduleIds,
                null,
                this.searchTerm,
                this.sort?.active,
                this.sort?.direction,
                this.paginator?.pageSize,
                this.paginator?.pageIndex + 1,
                this.isUnassignedSelectedModules,
                null,
                true,
                null,
                this.experienceIds,
                this.audienceIds,
                this.teamIds,
                this.extendedAttributesFilters,
                this.isUnassignedSelectedAudiences,
                this.isUnassignedSelectedExperiences,
                this.isUnassignedSelectedTeams,
                this.displayedColumns,
                this.userRoleIds,
                null,
                [],
                this.statusIds,
                this.excludedTeamIds,
                this.teamIds.length ? this.teamRoleIds : []
            );
        }
    }

    async startConversation(user: User): Promise<void> {
        await this._talkService.startConversationByUser(user, 'all_users_row');
    }
}
